/**
 * Notificação de "precisa de ação" fora da aba do Ops.
 * Spec: docs/superpowers/specs/2026-08-26-tickets-fila-notificacoes-design.md §8.
 *
 * Sem backend: o sinal já existe (`needs_action` + o polling de `useMyPendingCount`).
 * O que faltava era ALCANCE quando a aba do Ops não está em foco. Três camadas, da
 * mais barata à mais intrusiva:
 *   1. badge no `document.title` — visível na barra de abas sem foco, sem permissão;
 *   2. notificação nativa do SO — exige permissão, alcança browser minimizado;
 *   3. barra dentro da página — já existia, cobre quem está olhando a tela.
 *
 * Limite conhecido e aceito: tudo depende de uma aba do Ops viva em algum lugar
 * (qualquer tela — o indicador é do Topbar). Aba descartada ou máquina dormindo, para.
 */

// Casa também o badge saturado "(99+)".
const BADGE_RE = /^\(\d+\+?\)\s*/;

// Fixa: faz o SO SUBSTITUIR o toast anterior em vez de empilhar um por poll.
const TAG = 'ops-tickets-needs-action';

/** Remove um badge já aplicado, para não empilhar "(3) (2) Ops Panel". */
export const stripTitleBadge = (title: string): string =>
  title.replace(BADGE_RE, '');

/** "(3) Mecanizou Ops Panel". Zero remove o badge. */
export const titleWithBadge = (title: string, count: number): string => {
  const base = stripTitleBadge(title);
  return count > 0 ? `(${count > 99 ? '99+' : count}) ${base}` : base;
};

/**
 * "Longe" = outra aba (hidden) OU outro app, com a aba visível mas sem foco.
 * Só aí a notificação nativa faz sentido: quem está olhando a tela já recebe a
 * barra da página, e um toast do SO em cima disso vira ruído.
 */
export const isAway = (
  visibility: DocumentVisibilityState,
  hasFocus: boolean
): boolean => visibility === 'hidden' || !hasFocus;

export const pendingNotificationText = (
  delta: number,
  total: number
): {title: string; body: string} => ({
  title:
    delta === 1
      ? 'Novo ticket precisa da sua ação'
      : `${delta} novos tickets precisam da sua ação`,
  body:
    total === 1
      ? 'Você tem 1 ticket pendente no Ops.'
      : `Você tem ${total} tickets pendentes no Ops.`,
});

export const canNotify = (): boolean =>
  typeof window !== 'undefined' && 'Notification' in window;

// null = navegador sem suporte.
export const notificationPermission = (): NotificationPermission | null =>
  canNotify() ? Notification.permission : null;

const DISMISS_KEY = 'ops.tickets.notifyOptIn.dismissed';

// localStorage lança em aba anônima / cookies bloqueados: tratar como "não dispensado"
// é o padrão seguro (no pior caso a pessoa vê o convite de novo).
export const optInDismissed = (): boolean => {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
};

export const dismissOptIn = (): void => {
  try {
    localStorage.setItem(DISMISS_KEY, '1');
  } catch {
    // Sem persistência: o convite volta na próxima sessão. Aceitável.
  }
};

/**
 * O convite só aparece quando ainda dá para pedir (`default`) e a pessoa não dispensou.
 * Concedido ou bloqueado, não há o que oferecer — em `denied` o prompt nativo nem
 * reabre, só as configurações do site resolvem.
 */
export const shouldOfferNotifications = (
  permission: NotificationPermission | null,
  dismissed: boolean
): boolean => permission === 'default' && !dismissed;

/**
 * Dispara o prompt NATIVO. Só deve ser chamado a partir de um clique explícito no
 * convite — nunca no load nem num gesto qualquer.
 *
 * O motivo é que o prompt nativo é tiro único: um "bloquear" é grudento e só se
 * desfaz nas configurações do site. Perguntando antes na nossa própria UI, quem
 * chega ao prompt nativo já decidiu que quer — e as recusas ficam raras.
 */
export const requestNotificationPermission =
  async (): Promise<NotificationPermission | null> => {
    if (!canNotify()) return null;
    try {
      return await Notification.requestPermission();
    } catch {
      // Navegador antigo (API de callback): sem toast, resta o badge no título.
      return null;
    }
  };

/** Dispara o toast do SO. Devolve false quando não há permissão/suporte. */
export const notifyPending = (
  delta: number,
  total: number,
  onClick?: () => void
): boolean => {
  if (!canNotify() || Notification.permission !== 'granted') return false;
  const {title, body} = pendingNotificationText(delta, total);
  try {
    const n = new Notification(title, {body, tag: TAG, icon: '/favicon.ico'});
    n.onclick = () => {
      window.focus();
      n.close();
      onClick?.();
    };
    return true;
  } catch {
    return false;
  }
};
